import {Flag, Label, Option, Separator} from 'platform/components';
import {Grid, GridItem, Text, VStack} from 'platform/foundation';
import {useDateTimeFormatter} from 'platform/locale';

import {isNil} from 'ramda';

import {CustomerResponseBodyV2} from '@dms/api/customer';
import {
  BaseSupplierOrder,
  GetSupplierOrderVariantsResponse,
  GetWarehousesResponse,
} from '@dms/api/warehouse';
import i18n from '@dms/i18n';
import {getCustomerName} from '@dms/shared';
import {ReadOnlyField} from '@dms/shared-aftersales';

import {
  EMPTY_PLACEHOLDER,
  Nullish,
  parseDate,
  suffixTestId,
  TestIdProps,
  Unpacked,
} from 'shared/web';

import {getSupplierStateFlagProps} from '../utils/getSupplierStateFlagProps';

interface BasicInformationReadOnlyProps extends TestIdProps {
  supplierOrder: BaseSupplierOrder | Nullish;
  warehouse: Unpacked<GetWarehousesResponse> | Nullish;
  supplier: CustomerResponseBodyV2 | Nullish;
  supplierOrderType: Option | Nullish;
  supplierAccount: Option | Nullish;
  selectedSupplierOrderVariant: GetSupplierOrderVariantsResponse | Nullish;
}

export function BasicInformationReadOnly(props: BasicInformationReadOnlyProps) {
  const dateTimeFormatter = useDateTimeFormatter();

  const formatToShortDate = (date?: string) => {
    if (isNil(date)) {
      return EMPTY_PLACEHOLDER;
    }
    return dateTimeFormatter('dateShort', parseDate(date));
  };

  const formatToShortDateTime = (date?: string | Nullish) => {
    if (isNil(date)) {
      return EMPTY_PLACEHOLDER;
    }
    return dateTimeFormatter('dateTimeShort', parseDate(date));
  };

  const supplierOrderStateFlag = getSupplierStateFlagProps(props.supplierOrder?.supplierOrderState);

  return (
    <>
      <VStack spacing={4}>
        <Grid columns={4}>
          <ReadOnlyField
            label={i18n.t('entity.warehouse.labels.warehouse')}
            value={props.warehouse?.name}
          />
          <ReadOnlyField
            label={i18n.t('entity.warehouse.labels.supplierOrderVariant')}
            value={props.selectedSupplierOrderVariant?.name}
          />
          <ReadOnlyField
            label={i18n.t('entity.warehouse.labels.supplier')}
            value={getCustomerName(props.supplier)}
          />
          <ReadOnlyField
            label={i18n.t('entity.warehouse.labels.supplierOrderType')}
            value={props.supplierOrderType?.label}
          />
          <ReadOnlyField
            label={i18n.t('entity.warehouse.labels.supplierAccount')}
            value={props.supplierAccount?.label}
          />
        </Grid>
        <Grid columns={4}>
          <ReadOnlyField
            label={i18n.t('entity.warehouse.labels.currency')}
            value={props.supplierOrder?.currency}
          />
          <ReadOnlyField
            label={i18n.t('entity.warehouse.labels.exchangeRate')}
            value={props.supplierOrder?.exchangeRate}
          />
          <ReadOnlyField
            label={i18n.t('entity.warehouse.labels.ncConversion')}
            value={props.supplierOrder?.ncConversion}
          />
        </Grid>
        <Grid columns={4}>
          <GridItem span={4}>
            <ReadOnlyField
              label={i18n.t('entity.warehouse.labels.note')}
              value={props.supplierOrder?.note}
            />
          </GridItem>
        </Grid>
      </VStack>
      <Separator />
      <Grid columns={4}>
        <ReadOnlyField
          label={i18n.t('entity.warehouse.labels.sentDate')}
          value={formatToShortDate(props.supplierOrder?.orderedAt)}
        />
        <ReadOnlyField
          label={i18n.t('entity.warehouse.labels.createdDate')}
          value={formatToShortDate(props.supplierOrder?.created)}
        />
        <ReadOnlyField
          label={i18n.t('entity.warehouse.labels.updatedDate')}
          value={formatToShortDate(props.supplierOrder?.updated)}
        />
      </Grid>
      <Grid columns={4}>
        <VStack spacing={1}>
          <Label>{i18n.t('entity.warehouse.labels.supplierOrderState')}</Label>
          {supplierOrderStateFlag ? (
            <Flag
              colorScheme={supplierOrderStateFlag.colorScheme}
              label={supplierOrderStateFlag.label}
              data-testid={suffixTestId('supplierOrderState', props)}
            />
          ) : (
            <Text>{EMPTY_PLACEHOLDER}</Text>
          )}
        </VStack>
        <ReadOnlyField
          label={i18n.t('entity.warehouse.labels.supplierStateCheckedAt')}
          value={formatToShortDateTime(props.supplierOrder?.supplierStateCheckedAt)}
        />
        <ReadOnlyField
          label={i18n.t('entity.warehouse.labels.externalOrderRefNo')}
          value={props.supplierOrder?.referenceNumber}
        />
      </Grid>
    </>
  );
}
